/**
 * EditHistory - Edit Tracking and Undo
 *
 * Records AI edits with before-state snapshots, enabling undo
 * and providing edit context to LLM prompts.
 */

import { MMOCommand, CommandColor } from '../commands/CommandSchema';
import { FlowObjects } from '../../fsm/types';
import TextFlowObject from '../../../model/TextFlowObject';
import ShapeFlowObject from '../../../model/ShapeFlowObject';
import Screens from '../../../model/Screens';

export interface ElementSnapshot {
  mmoIndex: number;
  mmoId: string;
  properties: Record<string, any>;
}

export interface EditHistoryEntry {
  id: string;
  timestamp: number;
  userMessage: string;
  commands: MMOCommand[];
  screenIndex: number;
  previousState: ElementSnapshot[];
}

type EventSender = (event: any) => void;

const MAX_HISTORY = 50;

/**
 * Edit history manager, records AI edits and supports undo
 */
export class EditHistory {
  private entries: EditHistoryEntry[] = [];
  private nextId = 1;

  /**
   * Capture snapshots of properties that will be changed by commands
   */
  captureSnapshot(
    commands: MMOCommand[],
    screen: Screens,
    screenIndex: number
  ): ElementSnapshot[] {
    const mmos = screen.mixedMediaObjects || [];
    const snapshots: ElementSnapshot[] = [];
    const captured = new Set<number>();

    for (const cmd of commands) {
      if (cmd.type === 'screen_edit') {
        // Capture screen background
        const bgColor = screen.FlowScreenModelBackgroundColorKey;
        snapshots.push({
          mmoIndex: -1,
          mmoId: '__screen__',
          properties: {
            background: bgColor
              ? { r: bgColor.r, g: bgColor.g, b: bgColor.b, a: bgColor.a }
              : { r: 255, g: 255, b: 255, a: 1 },
          },
        });
        continue;
      }

      // Resolve the target index
      let mmoIndex = -1;
      if (cmd.target.mmoIndex !== undefined) {
        mmoIndex = cmd.target.mmoIndex;
      }

      if (mmoIndex < 0 || mmoIndex >= mmos.length || captured.has(mmoIndex)) continue;
      captured.add(mmoIndex);

      const mmo = mmos[mmoIndex];
      const props: Record<string, any> = {};

      // Always capture frame for position/size undo
      props['frame.x'] = mmo.frame.x;
      props['frame.y'] = mmo.frame.y;
      props['frame.width'] = mmo.frame.width;
      props['frame.height'] = mmo.frame.height;

      if (mmo.type === 'text') {
        const textMmo = mmo as TextFlowObject;
        props['font.fontSize'] = textMmo.font?.fontSize;
        props['font.fontName'] = textMmo.font?.fontName;
        if (textMmo.textColor) {
          props['textColor'] = {
            r: textMmo.textColor.r,
            g: textMmo.textColor.g,
            b: textMmo.textColor.b,
            a: textMmo.textColor.a,
          };
        }
        props['textAlignment'] = textMmo.textAlignment;
      } else if (mmo.type === 'shape') {
        const shapeMmo = mmo as ShapeFlowObject;
        if (shapeMmo.color) {
          props['color'] = {
            r: shapeMmo.color.r,
            g: shapeMmo.color.g,
            b: shapeMmo.color.b,
            a: shapeMmo.color.a,
          };
        }
      }

      snapshots.push({ mmoIndex, mmoId: mmo.id, properties: props });
    }

    return snapshots;
  }

  /**
   * Record an edit after execution
   */
  record(entry: Omit<EditHistoryEntry, 'id' | 'timestamp'>): string {
    const id = `edit-${this.nextId++}`;
    this.entries.push({
      ...entry,
      id,
      timestamp: Date.now(),
    });
    if (this.entries.length > MAX_HISTORY) {
      this.entries.shift();
    }
    return id;
  }

  /**
   * Undo the last edit by replaying the previous state
   */
  undo(send: EventSender): { success: boolean; message: string } {
    const entry = this.entries.pop();
    if (!entry) {
      return { success: false, message: 'Nothing to undo.' };
    }

    for (const snapshot of entry.previousState) {
      if (snapshot.mmoId === '__screen__' && snapshot.properties.background) {
        // Restore screen background
        const bg = snapshot.properties.background;
        send({
          type: 'SET_COLOR',
          color: { r: bg.r, g: bg.g, b: bg.b, a: bg.a },
        });
        continue;
      }

      const props = snapshot.properties;

      // Restore position
      if (props['frame.x'] !== undefined && props['frame.y'] !== undefined) {
        send({
          type: 'DELEGATE',
          id: snapshot.mmoId,
          payload: {
            type: 'SET_POSITION',
            x: props['frame.x'],
            y: props['frame.y'],
          },
        });
      }

      // Restore size
      if (props['frame.width'] !== undefined && props['frame.height'] !== undefined) {
        send({
          type: 'DELEGATE',
          id: snapshot.mmoId,
          payload: {
            type: 'SET_SIZE',
            width: props['frame.width'],
            height: props['frame.height'],
          },
        });
      }

      // Restore font size
      if (props['font.fontSize'] !== undefined) {
        send({
          type: 'DELEGATE',
          id: snapshot.mmoId,
          payload: { type: 'SET_FONT_SIZE', size: props['font.fontSize'] },
        });
      }

      // Restore font name
      if (props['font.fontName'] !== undefined) {
        send({
          type: 'DELEGATE',
          id: snapshot.mmoId,
          payload: { type: 'SET_FONT_NAME', typeface: props['font.fontName'] },
        });
      }

      // Restore text color
      if (props['textColor']) {
        send({
          type: 'DELEGATE',
          id: snapshot.mmoId,
          payload: { type: 'SET_COLOR', color: props['textColor'] },
        });
      }

      // Restore text alignment
      if (props['textAlignment'] !== undefined) {
        send({
          type: 'DELEGATE',
          id: snapshot.mmoId,
          payload: { type: 'SET_ALIGN', align: props['textAlignment'] },
        });
      }

      // Restore shape color
      if (props['color']) {
        send({
          type: 'DELEGATE',
          id: snapshot.mmoId,
          payload: { type: 'SET_COLOR', color: props['color'] },
        });
      }
    }

    return {
      success: true,
      message: `Undid: "${entry.userMessage}"`,
    };
  }

  /**
   * Check if undo is available
   */
  canUndo(): boolean {
    return this.entries.length > 0;
  }

  /**
   * Get a summary of recent edits for LLM context
   */
  getRecentSummary(count = 5): string {
    const recent = this.entries.slice(-count);
    if (recent.length === 0) return '';

    let out = 'RECENT EDITS:\n';
    recent.forEach((entry, i) => {
      const changes = this.summarizeCommands(entry);
      out += `${i + 1}. "${entry.userMessage}" -> ${changes}\n`;
    });
    return out;
  }

  /**
   * Summarize commands into a human-readable string
   */
  private summarizeCommands(entry: EditHistoryEntry): string {
    const parts: string[] = [];
    for (const snapshot of entry.previousState) {
      if (snapshot.mmoId === '__screen__') {
        parts.push('background changed');
        continue;
      }
      const propNames = Object.keys(snapshot.properties)
        .filter((k) => !k.startsWith('frame.'))
        .map((k) => k.replace('font.', ''));
      if (propNames.length > 0) {
        parts.push(`element[${snapshot.mmoIndex}] ${propNames.join(', ')} changed`);
      } else {
        parts.push(`element[${snapshot.mmoIndex}] position/size changed`);
      }
    }
    return parts.join('; ') || 'edit applied';
  }

  /**
   * Get the last entry (for display purposes)
   */
  getLastEntry(): EditHistoryEntry | undefined {
    return this.entries[this.entries.length - 1];
  }

  /**
   * Clear all history
   */
  clear(): void {
    this.entries = [];
  }
}
